/**
 * `/surveys` + `/surveys/:id` — khảo sát hài lòng của người học (TT 83 C9-c).
 */
import { Button, Card, Empty, List, Result, Spin, Typography, message } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import { getInvitationForm, listMySurveys, submitInvitation } from "@/features/surveys/api";
import { SurveyFormView } from "@/features/surveys/SurveyFormView";
import { formatDateTime } from "@/lib/formatDate";

const { Title } = Typography;

type ApiErr = { response?: { data?: { detail?: string } } };

export function Surveys() {
  const { id } = useParams();
  return id ? <SurveyAnswer id={Number(id)} /> : <SurveyList />;
}

function SurveyList() {
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["my-surveys"], queryFn: listMySurveys });
  return (
    <div style={{ maxWidth: 800, margin: "0 auto" }}>
      <Title level={2}>Khảo sát</Title>
      <Card>
        {q.isLoading ? (
          <Spin />
        ) : (q.data?.results.length ?? 0) === 0 ? (
          <Empty description="Không có khảo sát nào cần trả lời." />
        ) : (
          <List
            dataSource={q.data!.results}
            renderItem={(inv) => (
              <List.Item
                actions={[
                  <Button key="go" type="primary" onClick={() => navigate(`/surveys/${inv.id}`)}>
                    Trả lời
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={
                    inv.course_code
                      ? `${inv.course_code} — ${inv.course_name} · GV ${inv.instructor_name}`
                      : inv.campaign_name
                  }
                  description={`${inv.campaign_name} · hạn ${formatDateTime(inv.closes_at)}`}
                />
              </List.Item>
            )}
          />
        )}
      </Card>
    </div>
  );
}

function SurveyAnswer({ id }: { id: number }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useQuery({ queryKey: ["survey-form", id], queryFn: () => getInvitationForm(id) });
  const mut = useMutation({
    mutationFn: (answers: Parameters<typeof submitInvitation>[1]) => submitInvitation(id, answers),
    onSuccess: () => {
      message.success("Đã nộp phiếu — cảm ơn bạn!");
      queryClient.invalidateQueries({ queryKey: ["my-surveys"] });
      navigate("/surveys");
    },
    onError: (err: ApiErr) => message.error(err.response?.data?.detail ?? "Không nộp được phiếu"),
  });
  if (form.isLoading) return <Spin />;
  if (!form.data) return <Result status="info" title="Phiếu không còn mở hoặc bạn đã trả lời." />;
  return (
    <div style={{ maxWidth: 800, margin: "0 auto" }}>
      <SurveyFormView form={form.data} submitting={mut.isPending} onSubmit={mut.mutate} />
    </div>
  );
}
